import { HttpResponse } from '@capacitor/core';
import { JSONValue } from '../../../types/types';
import { resolveTemplate } from '../../../utils/json/resolveTemplate';
import { BoundErrorResponse } from '../../types/types';
import { isJsonResponse } from './isJsonResponse';
import { debugLogger } from '../../../debug/DebugLogger';

const log = debugLogger.scope('errorResponseTemplates');

// Reshapes the JSON error body via the errorResponse JSONata template before
// any error processing, so force (449), unavailable (503), timeout (504) and
// 4xx/5xx are covered. The template sees the same data as the request
// templates, with the error body added as "response". No-op without a
// template, on a success status, or for a non-JSON body.
export const processErrorResponseTemplate = async (
  response: HttpResponse,
  errorResponse?: BoundErrorResponse
): Promise<HttpResponse> => {
  if (!errorResponse?.template || response.status < 400) {
    return response;
  }

  if (!isJsonResponse(response)) {
    /** @debugFeature errorResponseTemplates Logs an errorResponse template skipped because the error body is not JSON — the template silently does not apply. */
    log('error response template skipped, non-JSON body', () => ({
      status: response.status,
      url: response.url,
    }));
    return response;
  }

  const { template, jsonData, customFunctions } = errorResponse;

  const error = await resolveTemplate(
    template,
    { ...jsonData, response: response.data as JSONValue },
    customFunctions
  );

  /** @debugFeature errorResponseTemplates Logs an applied errorResponse template: status, url, the original error body and the reshaped error. */
  log('error response template applied', () => ({
    status: response.status,
    url: response.url,
    originalBody: response.data,
    error,
  }));

  return {
    ...response,
    data: { error },
  };
};
